import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { ConfigEntityAuthAction } from '@web-config-app/core';
import type {
  DomainEntity,
  EnvironmentKey,
  GetEndpointResponse,
} from '@web-config-app/core';
import type { EnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { getEndpointParamsWithValues } from '../../utilities/get-endpoint-params-with-values/get-endpoint-params-with-values';
import { getIdParam } from '../../utilities/get-id-param/get-id-param';
import { getIncludeParam } from '../../utilities/get-include-param/get-include-param';

interface EntityGetQueryProps {
  entity: DomainEntity;
  instanceId?: string;
  environment: EnvironmentKey;
  getIncludedEntities?: boolean;
  isUsingConfigAppBackend?: boolean;
}

/**
 * The cache key for reading one entity from one environment.
 *
 * Keyed on everything that changes the request or the response's *shape*, not just its
 * contents: `getIncludedEntities` decides whether `included` comes back at all, and
 * `isUsingConfigAppBackend` which include list is asked for. Two reads that disagree on
 * either must not share an entry, or whichever resolves first serves the other.
 *
 * Exported so every reader derives the key from one place -- two callers hand-writing the
 * same tuple is how a cache silently splits, and the symptom is a stale action, not a build
 * failure.
 */
export const getEntityGetQueryKey = ({
  entity,
  instanceId,
  environment,
  getIncludedEntities,
  isUsingConfigAppBackend,
}: EntityGetQueryProps) => [
  environment,
  entity.endpoints.get.path,
  instanceId,
  entity.type,
  Boolean(getIncludedEntities),
  // Also shapes the request: it selects `include=allReferences` over the schema-derived
  // list. Keyed unconditionally, though only meaningful alongside getIncludedEntities, so a
  // future caller can't reintroduce the collision by omitting it.
  Boolean(isUsingConfigAppBackend && getIncludedEntities),
];

/**
 * The key and fetcher for reading one entity from one environment, so `useQuery` and
 * `useQueries` callers issue byte-identical requests against byte-identical cache entries.
 *
 * Takes the environment-bound fetch rather than resolving it: the caller decides whether
 * that's fixed for a component's lifetime or chosen per environment.
 */
export const getEntityGetQueryConfig = ({
  entity,
  instanceId,
  environment,
  getIncludedEntities,
  isUsingConfigAppBackend,
  environmentFetch,
}: EntityGetQueryProps & { environmentFetch: EnvironmentFetch }) => {
  const { get } = entity.endpoints;

  const params = getEndpointParamsWithValues([
    getIdParam(get.pathParameters ?? [], get.path, instanceId),
    getIncludeParam(
      get.queryParameters ?? [],
      entity.schema,
      getIncludedEntities ?? false,
      isUsingConfigAppBackend && getIncludedEntities
        ? 'allReferences'
        : undefined,
    ),
  ]);

  return {
    queryKey: getEntityGetQueryKey({
      entity,
      instanceId,
      environment,
      getIncludedEntities,
      isUsingConfigAppBackend,
    }),
    queryFn: () => {
      const { endpointFetch } = getEntityEndpoint<GetEndpointResponse>(
        get,
        environmentFetch,
      );
      return endpointFetch({
        params,
        headers: buildConfigEntityAuthHeaders(
          entity.type,
          ConfigEntityAuthAction.Get,
        ),
      });
    },
  };
};
